import React, { useState, useEffect } from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  ColumnActionsMode,
  SelectionMode,
  IColumn,
  ConstrainMode,
} from "@fluentui/react";
import MainView from "./SiteDetails/MainView";
import SiteHistoryModal from "./SiteDetails/SiteHistoryModal";
import { Spinner, SpinnerSize } from "@fluentui/react";
import Api from "../../services/Api";
import { useHistory } from "react-router-dom";

const SiteList = ({
  isLoading,
  results,
  filters,
  orderedBy,
  sortItems,
  modalId,
}) => {
  const [items, setItems] = useState(results);
  const [modalOpened, setModalOpened] = useState(true);

  useEffect(() => {
    if (results && modalId) {
      setItems([...results]);
    } else if (results) {
      setItems(results);
    }
  }, [results]);

  const initialColumns: IColumn[] = [
    {
      key: "title",
      name: "Site Name",
      fieldName: "title",
      minWidth: 100,
      maxWidth: 150,
      isRowHeader: false,
      isResizable: true,
      data: "string",
      isPadded: true,
    },
    {
      key: "url",
      name: "Site URL",
      fieldName: "url",
      minWidth: 100,
      maxWidth: 360,
      isResizable: true,
      data: "string",
      onRender: (item) => _renderUrl(item.url),
      isPadded: true,
    },
    {
      key: "owner",
      name: "Owner",
      fieldName: "owner",
      minWidth: 130,
      maxWidth: 200,
      isResizable: true,
      data: "string",
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => _renderOwners(item.owners),
      isPadded: true,
    },
    {
      key: "expirationDate",
      name: "Expiration Date",
      fieldName: "expirationDate",
      minWidth: 60,
      maxWidth: 100,
      isResizable: true,
      data: "number",
      onRender: (item) => {
        return (
          item.isRdArchive ?
          <div></div>
          :
          <span>
            {new Date(item.expirationDate + "Z").toLocaleDateString()}
          </span>
          
        );
      },
      isPadded: true,
    },
    {
      key: "siteStatus",
      name: "Site Status",
      fieldName: "siteStatus",
      minWidth: 60,
      maxWidth: 150,
      isResizable: true,
      data: "string",
      isPadded: true,
    },
    {
      key: "provisioningStatus",
      name: "Provisioning Status",
      fieldName: "provisioningStatus",
      minWidth: 60,
      maxWidth: 150,
      isResizable: true,
      data: "string",
      isPadded: true,
    },
    {
      key: "externalCollaboration",
      name: "External Collaboration",
      fieldName: "externalCollaboration",
      minWidth: 22,
      maxWidth: 50,
      isResizable: true,
      data: "string",
      onRender: (item) => {
        return <span>{item.externalCollaboration ? "Yes" : "No"}</span>;
      },
      isPadded: true,
    },
    {
      key: "confidentiality",
      name: "Confidentiality",
      fieldName: "confidentiality",
      minWidth: 60,
      maxWidth: 120,
      isResizable: true,
      data: "string",
      isPadded: true,
    },
    {
      key: "management",
      name: "Organization",
      minWidth: 30,
      maxWidth: 100,
      isResizable: true,
      data: "string",
      onRender: (item) => {
        return (
          <span>
            {item.dimension &&
              item.dimension.management &&
              item.dimension.management.key}
          </span>
        );
      },
      isPadded: true,
    },
    {
      key: "geography",
      name: "Site Audience",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      data: "string",
      onRender: (item) => {
        return (
          <span>
            {item.dimension &&
              item.dimension.geography &&
              item.dimension.geography.value}
          </span>
        );
      },
      isPadded: true,
    },
    {
      key: "edit",
      name: "Actions",
      minWidth: 64,
      maxWidth: 64,
      isResizable: false,
      isIconOnly: true,
      isPadded: true,
      columnActionsMode: ColumnActionsMode.disabled,
      onRender: (item) => {
        return <MainView editedSite={item} callBack={_onEditCallback} />;
      },
    },
  ];
  const history = useHistory();

  const onCloseHistoryModal = () => {
    setModalOpened(false);
    history.push("/Administration");
  };

  const _getSiteById = async (id) => {
    const result = await Api.get(`/Site?id=${id}`);

    return result.data;
  };

  const _onEditCallback = async (id) => {
    const site = await _getSiteById(id);

    const updatedItems = items.map((x) => (x.id === id ? site : x));

    setItems(updatedItems);
  };

  const _renderOwners = (owners) => {
    return owners.map((x, index) => (
      <div key={x.id || `${x.email}-${index}`}>{x.email}</div>
    ));
  };

  const _renderUrl = (url) => {
    return (
      <div>
        <a
          href={url}
          title={url}
          target="_blank"
          style={{ color: "inherit", textDecoration: "none" }}
        >
          {url}
        </a>
      </div>
    );
  };

  const _onColumnClick = (ev, column) => {
    sortItems(column.fieldName, !column.isSortedDescending);
  };

  const getColumns = (initialColumns) => {
    const columns = initialColumns.map((c) => {
      c.isFiltered = (filters[c.fieldName] || "").length > 0;
      c.isSorted = orderedBy.fieldName === c.fieldName;
      c.isSortedDescending = c.isSorted ? orderedBy.isDescending : true;
      return c;
    });

    return columns;
  };

  const columns = getColumns(initialColumns);
  const spinner = (
    <Spinner style={{ margin: "200px" }} size={SpinnerSize.large} />
  );

  return (
    <div>
      {!isLoading && window.location.hash ? (
        <SiteHistoryModal
          site={results[0]}
          isModalOpened={modalOpened}
          onCloseModal={onCloseHistoryModal}
        />
      ) : (
        ""
      )}
      {isLoading ? (
        spinner
      ) : (
        <DetailsList
          items={items}
          columns={columns}
          layoutMode={DetailsListLayoutMode.justified}
          isHeaderVisible={true}
          selectionMode={SelectionMode.none}
          styles={{ root: { width: "100%" } }}
          onColumnHeaderClick={_onColumnClick}
          constrainMode={ConstrainMode.unconstrained}
          getKey={(item, index) => item.id || item.url || index}
        />
      )}
    </div>
  );
};

export default SiteList;



// WEBPACK FOOTER //
// ./src/components/Administration/SiteList.js